import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Braces, ChevronDown, Code2, File, FileText, Film, Image as ImageIcon, Lock, } from 'lucide-react';
import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Markdown, safeUrl } from '../lib/markdown.js';
import { cx, STATE_LABEL, StatusGlyph } from './primitives.js';
import { Sheet } from './Sheet.js';
const KIND = {
    image: { icon: ImageIcon, label: 'Image' },
    video: { icon: Film, label: 'Video' },
    html: { icon: Code2, label: 'HTML' },
    markdown: { icon: FileText, label: 'Markdown' },
    json: { icon: Braces, label: 'JSON' },
    text: { icon: FileText, label: 'Text' },
    pdf: { icon: File, label: 'PDF' },
    file: { icon: File, label: 'File' },
};
export function artifactKind(mime, name = '') {
    const m = (mime ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
    const ext = /\.([a-z0-9]+)$/i.exec(name)?.[1]?.toLowerCase() ?? '';
    if (m.startsWith('image/'))
        return 'image';
    if (m.startsWith('video/'))
        return 'video';
    if (m === 'text/html')
        return 'html';
    if (m === 'text/markdown' || ext === 'md' || ext === 'markdown')
        return 'markdown';
    if (m === 'application/json' || ext === 'json')
        return 'json';
    if (m === 'application/pdf' || ext === 'pdf')
        return 'pdf';
    if (m.startsWith('text/') || ['txt', 'log', 'csv', 'diff', 'patch'].includes(ext))
        return 'text';
    return 'file';
}
/** Kinds whose content the card can show as text. */
export const TEXT_KINDS = new Set(['markdown', 'json', 'text']);
/**
 * Title + description from the caption ("viko doctor — new calm rendering…" → title "vikod
 * doctor", description the rest); the file name when there is no caption.
 */
export function artifactTitle(a) {
    const cap = a.caption?.trim();
    if (!cap)
        return { title: a.name };
    const m = /^(.{1,80}?)\s+[—–]\s+([\s\S]+)$/.exec(cap);
    if (m?.[1] && m[2])
        return { title: m[1], description: m[2] };
    return { title: cap };
}
/** Exact-enough byte size: one decimal under 10 (3.8 KB), whole units above; bytes in the title. */
export function artifactSize(n) {
    if (n < 1024)
        return `${n} B`;
    const [v, u] = n < 1024 * 1024 ? [n / 1024, 'KB'] : [n / 1024 / 1024, 'MB'];
    return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${u}`;
}
const PREVIEW_CHARS = 200_000;
/** Text of a `data:` URL (only inline data is read; remote URLs are never fetched here). */
export function dataUrlText(url) {
    if (!url?.startsWith('data:'))
        return undefined;
    const comma = url.indexOf(',');
    if (comma < 0)
        return undefined;
    const meta = url.slice(5, comma);
    const payload = url.slice(comma + 1);
    try {
        if (/;base64$/i.test(meta)) {
            const bin = atob(payload);
            const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
            return new TextDecoder().decode(bytes);
        }
        return decodeURIComponent(payload);
    }
    catch {
        return undefined;
    }
}
/** Plain-text/markdown/JSON body for an artifact preview. Rendered as text, never as HTML. */
export function ArtifactText({ text, kind }) {
    const clipped = text.length > PREVIEW_CHARS;
    const body = clipped ? text.slice(0, PREVIEW_CHARS) : text;
    return (_jsxs(_Fragment, { children: [kind === 'markdown' ? (_jsx(Markdown, { text: body, className: "vk-artcard__md" })) : (_jsx("pre", { className: "vk-artcard__text vk-mono", children: body })), clipped ? (_jsxs("p", { className: "vk-meta", children: ["Showing the first ", PREVIEW_CHARS.toLocaleString('en-US'), " of", ' ', text.length.toLocaleString('en-US'), " characters."] })) : null] }));
}
function BuiltinPreview({ artifact, kind }) {
    const [zoom, setZoom] = useState(false);
    const text = useMemo(() => (TEXT_KINDS.has(kind) ? dataUrlText(artifact.url) : undefined), [artifact.url, kind]);
    const src = kind === 'image'
        ? (artifact.url ?? artifact.thumbUrl)
        : artifact.url?.startsWith('data:image/')
            ? artifact.url
            : undefined;
    if (text !== undefined)
        return _jsx(ArtifactText, { text: text, kind: kind });
    if (src)
        return (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: "vk-artcard__zoom", "aria-label": `Open ${artifact.name} full size`, onClick: () => setZoom(true), children: _jsx("img", { src: src, alt: artifact.caption ?? artifact.name, className: "vk-artcard__img" }) }), _jsx(Sheet, { open: zoom, onClose: () => setZoom(false), title: artifact.name, subtitle: artifact.caption, placement: "center", size: "lg", children: _jsx("div", { className: "vk-lightbox", children: _jsx("img", { src: artifact.url ?? src, alt: artifact.caption ?? artifact.name, className: "vk-lightbox__media" }) }) })] }));
    if (kind === 'video' && artifact.url)
        return (_jsx("video", { src: artifact.url, poster: artifact.thumbUrl, controls: true, className: "vk-artcard__img", "aria-label": artifact.name }));
    if (kind === 'html' && artifact.url)
        return (_jsx("iframe", { title: artifact.name, src: artifact.url, sandbox: "", className: "vk-artcard__frame" }));
    const href = artifact.url ? safeUrl(artifact.url) : null;
    return (_jsxs("p", { className: "vk-meta", children: ["No inline preview for this file.", href && !href.startsWith('data:') ? (_jsxs(_Fragment, { children: [' ', _jsxs("a", { className: "vk-md-link", href: href, target: "_blank", rel: "noopener noreferrer", children: ["Open ", artifact.name] })] })) : null] }));
}
/**
 * An artifact in the transcript: type, title, clamped description, path and size, with an
 * expandable preview. Images and videos open expanded; text-like files expand on demand.
 * `preview` replaces the built-in preview (the web app injects its encrypted endpoint renderer,
 * mounted only while expanded); `locked` shows the metadata without any media.
 */
export function ArtifactCard({ artifact, state = 'ok', preview, defaultOpen, locked = false, }) {
    const kind = artifactKind(artifact.mime, artifact.name);
    const { icon: Icon, label } = KIND[kind];
    const { title, description } = artifactTitle(artifact);
    const media = kind === 'image' || kind === 'video';
    const [open, setOpen] = useState(defaultOpen ?? (media && !locked));
    const [full, setFull] = useState(false);
    const desc = useRef(null);
    const [clamped, setClamped] = useState(false);
    // Offer the full description only when the two-line clamp actually hides text.
    useLayoutEffect(() => {
        const el = desc.current;
        if (!el || full)
            return;
        const check = () => setClamped(el.scrollHeight > el.clientHeight + 1);
        check();
        if (typeof ResizeObserver === 'undefined')
            return;
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => ro.disconnect();
    }, [full]);
    const titleId = useId();
    const bodyId = useId();
    const busy = state === 'pending' || state === 'streaming';
    const canPreview = !locked && !busy;
    return (_jsxs("figure", { className: cx('vk-artcard', `vk-artcard--${kind}`, `vk-artcard--${state}`, open && 'is-open'), "aria-labelledby": titleId, children: [_jsxs("div", { className: "vk-artcard__head", children: [_jsx("span", { className: "vk-artcard__icon", "aria-hidden": "true", children: locked ? _jsx(Lock, { size: 16 }) : _jsx(Icon, { size: 17 }) }), _jsxs("div", { className: "vk-artcard__main", children: [_jsxs("div", { className: "vk-artcard__top", children: [_jsx("span", { className: "vk-artcard__kind", children: label }), artifact.bytes != null ? (_jsx("span", { className: "vk-artcard__size", title: `${artifact.bytes.toLocaleString('en-US')} bytes`, children: artifactSize(artifact.bytes) })) : null, state !== 'ok' ? (_jsxs("span", { className: "vk-artcard__state", children: [_jsx(StatusGlyph, { state: state, size: 12 }), state === 'streaming' ? 'Uploading' : STATE_LABEL[state]] })) : null] }), _jsx("div", { id: titleId, className: "vk-artcard__title", children: title }), description ? (_jsx("p", { ref: desc, className: cx('vk-artcard__desc', !full && 'is-clamped'), children: description })) : null, description && (clamped || full) ? (_jsx("button", { type: "button", className: "vk-artcard__more", "aria-expanded": full, onClick: () => setFull(!full), children: full ? 'Show less' : 'Show full description' })) : null, artifact.path || (description || artifact.caption ? artifact.name !== title : false) ? (_jsx("code", { className: "vk-artcard__path vk-mono", children: artifact.path ?? artifact.name })) : null, locked ? (_jsx("span", { className: "vk-artcard__locked", role: "status", children: "Attachment locked or unavailable" })) : null] }), canPreview ? (_jsxs("button", { type: "button", className: "vk-artcard__toggle", "aria-expanded": open, "aria-controls": bodyId, onClick: () => setOpen(!open), children: [_jsx("span", { children: open ? 'Hide preview' : 'Preview' }), _jsx(ChevronDown, { size: 14, "aria-hidden": "true", className: cx('vk-chev', open && 'is-open') })] })) : null] }), open && canPreview ? (_jsx("div", { id: bodyId, className: "vk-artcard__preview", children: preview ?? _jsx(BuiltinPreview, { artifact: artifact, kind: kind }) })) : null] }));
}
//# sourceMappingURL=ArtifactCard.js.map